import APIAxios, {APIRoutes} from '@api/axios.api';
import {GridSortItem} from '@mui/x-data-grid-pro';
import {UIEntitiesRef, updateAllEntities, upsertEntities, upsertEntitiesById} from '@ngneat/elf-entities';
import {setPage, updatePaginationData} from '@ngneat/elf-pagination';
import {Pagination} from '@store/common/pagination.model';
import {TableFilter} from '@store/common/tableFilter.model';
import {filesService} from '@store/files';
import {ExecutionLine, InvoiceLineUpdateDto} from '@store/invoices/executionLines';
import {
  BillsGroupedAction,
  InvoiceCreation,
  InvoiceCreationBase,
  InvoiceDetails,
  InvoiceForMailing,
  InvoicesFilters,
  InvoicesPagination,
  InvoiceStatus,
  SendInvoiceForMailing,
  SendInvoiceType
} from '@store/invoices/invoices/invoices.model';
import {getInvoicesDataSource, postBillDataSource} from '@store/invoices/invoices/invoices.requests';
import {invoicesStore} from '@store/invoices/invoices/invoices.store';
import {InvoiceLine} from '@store/invoices/purchaseStatements';
import SnackError from '@utils/error.utils';
import {extractFilenameFromResponse} from '@utils/response.utils';
import {AxiosError, AxiosResponse} from 'axios';
import {catchError, from, map, Observable, switchMap, tap} from 'rxjs';
import {invoicesQuery} from '@store/invoices/invoices/invoices.query';
import {setProp} from '@ngneat/elf';

export class InvoicesService {
  store = invoicesStore;

  setSort = (sort: GridSortItem) => this.store.update((state) => ({...state, sort}));
  setPagination = (name: string) => (value: number) => this.store.update((state) => ({
    ...state,
    tablePagination: {
      ...state.tablePagination,
      [name]: value,
    },
  }));

  resetFilters = () => this.store.update((state) => ({
    ...state,
    filters: {search: '', tableFilters: InvoicesFilters.initTableFilters(), filterAccountantTransferred: false},
  }));
  setFilters = (name: string) => (value: any) => this.store.update((state) => ({
    ...state,
    filters: {
      ...state.filters,
      [name]: value,
    },
  }));

  getInvoices = (): Observable<InvoicesPagination> => {
    const {
      filters: {search, tableFilters, statuses, from: dateFrom, to, filterAccountantTransferred},
      sort,
      tablePagination
    } = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    const params = {
      ...requestFilters,
      searchString: search || undefined,
      page: tablePagination.page,
      limit: tablePagination.limit,
      sortBy: sort.field,
      orderBy: sort.sort?.toLocaleUpperCase(),
      periodFrom: dateFrom,
      periodTo: to,
      hideTransferred: filterAccountantTransferred,
    };

    if (statuses?.length)
      params['statuses'] = statuses.join(',');

    return from(APIAxios({
      ...APIRoutes.GETInvoices(),
      params: params,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<InvoicesPagination>) => {
          return response.data;
        }),
        tap((invoicePagination) => {
          const paginationData = Pagination.toPaginationData(invoicePagination, tablePagination.page);

          this.store.update(
            upsertEntities(invoicePagination.data),
            upsertEntitiesById(invoicePagination.data.map((invoice) => invoice.id), {
              updater: (invoiceUi) => invoiceUi,
              creator: (invoiceId) => ({id: invoiceId, selected: false}),
              ref: UIEntitiesRef,
            }),
            updatePaginationData(paginationData),
            setPage(paginationData.currentPage, invoicePagination.data.map((invoice) => invoice.id)),
            setProp('sumNoVatInCents', invoicePagination.sumNoVatInCents),
            getInvoicesDataSource.setSuccess(),
          );
        }),
        getInvoicesDataSource.trackRequestStatus(),
      );
  };

  getInvoiceDetails = (invoiceId: string): Observable<InvoiceDetails> => {
    return from(APIAxios({
      ...APIRoutes.GETInvoiceDetails(invoiceId),
    })).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<InvoiceDetails>) => {
        return response.data;
      })
    );
  };

  patchInvoiceLine = (invoiceId: number | string, invoiceLineId: number, updatedInvoiceLine: Partial<ExecutionLine>): Observable<any> => {
    const data: InvoiceLineUpdateDto = {
      unitCostInCents: updatedInvoiceLine.puBuy,
      unitPriceInCents: updatedInvoiceLine.puSell,
      quantity: updatedInvoiceLine.quantity,
      name: updatedInvoiceLine.name,
    };
    return from(APIAxios({
      ...APIRoutes.PATCHInvoiceInvoiceLine(invoiceId, invoiceLineId),
      data
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
      );
  };

  updateInvoice = (invoiceId: string, status?: InvoiceStatus, comment?: string): Observable<InvoiceDetails> => {
    return from(APIAxios({
      ...APIRoutes.PATCHInvoice(invoiceId),
      data: {
        status: status,
        comment: comment
      }
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getInvoiceDetails(invoiceId)),
      );
  };

  updateInvoiceAddress(id: number | string, data: any) {
    return from(APIAxios({
      ...APIRoutes.PATCHInvoiceAddress(id),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<InvoiceDetails>) => {
          return response.data;
        })
      );
  }

  setSelectedInvoices = (invoiceIds: number[]) => {
    this.store.update(
      updateAllEntities(
        (invoiceUI) => ({
          ...invoiceUI,
          selected: invoiceIds.includes(invoiceUI.id)
        }),
        {ref: UIEntitiesRef},
      ),
    );
  };

  postInvoice = (data: Partial<InvoiceCreationBase>, forNestle: boolean): Observable<any> => {
    return from(APIAxios({
      ...(forNestle ? APIRoutes.POSTInvoiceNestle() : APIRoutes.POSTInvoice()),
      data
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        tap(() => {
          this.store.update(
            // updateEntities(executionLineId, () => resp),
            postBillDataSource.setSuccess(),
          );
        }),
        postBillDataSource.trackRequestStatus(),
        map((res) => {
          return res.data;
        }),
      );
  };

  postInvoiceFromLines = (selectedIds: number[], bill: Partial<InvoiceCreation>) => {
    return from(APIAxios({
      ...APIRoutes.POSTInvoiceFromLines(),
      data: {
        execLineIds: selectedIds,
        invoicingRule: bill.invoicingRule,
        documentDate: bill.documentDate,
        reminderDate: bill.reminderDate,
        includeAdminFee: bill.includeAdminFee,
      }
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        tap(() => {
          this.store.update(
            postBillDataSource.setSuccess(),
          );
        }),
        postBillDataSource.trackRequestStatus(),
        map((res) => {
          return res.data;
        }),
      );
  };

  deleteInvoice = (id: number | string): Observable<any> => {
    return from(APIAxios({
      ...APIRoutes.DELETEInvoice(id),

    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
      );
  };

  printInvoice = (id: number | string): Observable<any> => {
    return from(APIAxios({
      ...APIRoutes.GETInvoicePdf(id),
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        tap((response) => {
          const filename = extractFilenameFromResponse(response);
          filesService.downloadDocument(response.data, filename || `invoice-${id}.pdf`);
        }),
      );
  };

  printInvoices(ids: number[]): Observable<any> {
    return from(APIAxios({
      ...APIRoutes.GETInvoiceMergePdf(),
      params: {
        ids: ids.join(',')
      }
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        tap((response) => {
          const filename = extractFilenameFromResponse(response);
          filesService.downloadDocument(response.data, filename || 'invoices.pdf');
        }),
      );
  }

  downloadInvoicesZIP(ids: number[]): Observable<any> {
    return from(APIAxios({
      ...APIRoutes.GETInvoiceZipPdf(),
      params: {
        ids: ids.join(',')
      }
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        tap((response) => {
          const filename = extractFilenameFromResponse(response);
          filesService.downloadDocument(response.data, filename || 'invoices.zip');
        }),
      );
  }

  getInvoicesPiecesCSV() {
    const {filters: {search, tableFilters, statuses, from: dateFrom, to}, sort, tablePagination} = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    const params = {
      ...requestFilters,
      searchString: search || undefined,
      page: tablePagination.page,
      limit: tablePagination.limit,
      sortBy: sort.field,
      orderBy: sort.sort?.toLocaleUpperCase(),
      periodFrom: dateFrom,
      periodTo: to,
    };

    if (statuses?.length)
      params['statuses'] = statuses.join(',');

    return from(APIAxios({
      ...APIRoutes.GETInvoicesPiecesCSV(),
      params: params,
      responseType: 'blob',
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        tap((response: AxiosResponse) => {
          const filename = extractFilenameFromResponse(response);
          filesService.downloadDocument(response.data, filename || 'export.csv');
        }),
      );
  }

  getInvoicesCSV() {
    const {filters: {search, tableFilters, statuses, from: dateFrom, to}, sort, tablePagination} = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    const params = {
      ...requestFilters,
      searchString: search || undefined,
      page: tablePagination.page,
      limit: tablePagination.limit,
      sortBy: sort.field,
      orderBy: sort.sort?.toLocaleUpperCase(),
      periodFrom: dateFrom,
      periodTo: to,
    };

    if (statuses?.length)
      params['statuses'] = statuses.join(',');

    return from(APIAxios({
      ...APIRoutes.GETInvoicesCSV(),
      params: params,
      responseType: 'blob',
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        tap((response: AxiosResponse) => {
          const filename = extractFilenameFromResponse(response);
          filesService.downloadDocument(response.data, filename || 'export.csv');
        }),
      );
  }

  invoiceLinesCsv = (invoiceId: string): Observable<any> => {
    return from(APIAxios({
      ...APIRoutes.GETInvoiceInvoiceLinesCSV(invoiceId),
      responseType: 'blob',
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        tap((response: AxiosResponse) => {
          const headerLine = response.headers['content-disposition'];
          const startFileNameIndex = headerLine?.indexOf('"') + 1;
          const endFileNameIndex = headerLine?.lastIndexOf('"');
          let filename;
          if (startFileNameIndex && endFileNameIndex) {

            filename = headerLine.substring(startFileNameIndex, endFileNameIndex);
          }

          filesService.downloadDocument(response.data, filename || 'export.csv');
        }),
      );
  };

  addInvoiceInvoiceLine = (id: number | string, invoiceLine: InvoiceLine): Observable<InvoiceDetails> => {
    return from(APIAxios({
      ...APIRoutes.PUTInvoiceOrphanLine(id),
      data: invoiceLine,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<InvoiceDetails>) => response.data),
      );
  };

  removeInvoiceInvoiceLines = (billId: number | string, invoiceLinesIds: number[], revertLines: boolean): Observable<AxiosResponse> => {
    return from(APIAxios({
      ...APIRoutes.DELETEInvoiceInvoiceLines(billId),
      data: {invoiceLinesIds, revertLines},
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
      );
  };

  getInvoicesToSend = (ids: number[]): Observable<InvoiceForMailing[]> => {
    return from(APIAxios({
      ...APIRoutes.GETInvoicesToSend(),
      params: {ids: ids.join(',')},
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<InvoiceForMailing[]>) => {
          return response.data;
        }),
      );
  };

  sendInvoices = (invoices: SendInvoiceForMailing[], sendInvoiceType: SendInvoiceType): Observable<AxiosResponse> => {
    return from(APIAxios({
      ...APIRoutes.POSTSendInvoices(),
      data: {invoices, type: sendInvoiceType},
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
      );
  };

  cancelInvoice(invoiceId: number, selectedInvoiceLines: number[], reason: string) {
    return from(APIAxios({
      ...APIRoutes.POSTCancelInvoice(invoiceId),
      data: {
        cancellationReason: reason,
        invoiceLineIds: selectedInvoiceLines
      }
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<InvoiceDetails>) => response.data),
      );
  }

  groupedAction(data: BillsGroupedAction) {
    const ids = invoicesQuery.selectedBills;
    return from(APIAxios({
      ...APIRoutes.PATCHGroupedAction(),
      data: {
        ids,
        ...data,
      }
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getInvoices())
      );
  }

  resetAccountingTransferDate(invoiceId: string) {
    return from(APIAxios({
      ...APIRoutes.PATCHInvoiceResetAccountingTransferDate(invoiceId),
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<InvoiceDetails>) => response.data),
      );
  }
}

export const invoicesService = new InvoicesService();
